import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

interface GraphNode {
  id: string;
  label: string;
  type: 'object' | 'person' | 'terminal' | 'task' | 'place' | 'fact';
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  color: string;
}

interface GraphEdge {
  source: string;
  target: string;
  type: 'detected' | 'synced' | 'derived' | 'assigned' | 'located' | 'linked';
  /**
   * A MEASURED value for this edge, or undefined when the data has none.
   *
   * ★★ What used to be here: every edge got a label hashed out of its own name —
   *    `latency = 2 + hash*98` ms, `距离 = 1 + hash*49` m, `置信度 = 60 + hash*40`%.
   *    They were stable per edge, so they looked like readings; they were shown on
   *    ALL 81 edges at default zoom, so the graph read as a dense telemetry display.
   *    Not one of those numbers came from the dog. On a page whose whole subject is
   *    "what the fleet actually knows", that is the worst possible thing to draw.
   *
   *    Now: distance is computed from the two real map-frame coordinates,
   *    observation counts and fact confidence come from the device's own records,
   *    and an edge with no such quantity gets NO label instead of a plausible one.
   */
  metric?: string;
}

interface Props {
  objects: {
    id: string; name: string; detectedBy: string; confidence: number; location: string;
    nObs?: number; deviceId?: string;
    /** map-frame coordinates, if the device reported them — used for real distances. */
    x?: number; y?: number;
  }[];
  /** PIPL:人物数据永不上云 —— 该字段保留只为组件兼容,页面永远不传。 */
  people?: { id: string; name: string; syncedTerminals: string[] }[];
  terminals: { id: string; name: string; type: string }[];
  facts: { id: string; content: string; source: string; deviceId?: string; confidence?: number }[];
  tasks?: { id: string; name: string; assignees: string[]; status: string }[];
  places: { id: string; name: string; room: string; placeType: string; deviceId?: string; x?: number; y?: number }[];
  /** device = 群体视图按归属设备着色;type(默认)= 按实体类型着色。 */
  colorMode?: 'type' | 'device';
  deviceColors?: Record<string, string>;
}

const NODE_COLORS: Record<string, string> = {
  object: '#f59e0b',
  person: '#10b981',
  terminal: '#6366f1',
  task: '#06b6d4',
  place: '#f97316',
  fact: '#ec4899',
};

const EDGE_FLOW_COLORS: Record<string, string> = {
  detected: 'rgba(245,158,11,0.7)',
  synced: 'rgba(99,102,241,0.7)',
  derived: 'rgba(236,72,153,0.7)',
  assigned: 'rgba(6,182,212,0.7)',
  located: 'rgba(249,115,22,0.7)',
  linked: 'rgba(148,163,184,0.5)',
};

/* Convert hex color to rgba */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * 布局迭代数。★★ 关键: 这些步是【一次算完】的, 不是每帧推两步。
 *
 * 之前的做法是把力导挂在 rAF 上, 每帧推 2 步、每帧整幅重画, 直到"稳定"。
 * 三个实测数字说明它为什么不可能安静下来(2026-08-08, 加了逐帧诊断才看见):
 *   · 循环实际只跑到 **5.8 帧/秒** —— 62 个节点的 O(n²) 斥力 + 62 个径向渐变,
 *     一帧的活儿太重;
 *   · 于是 900 步的上限要 **77 秒**才够得到, 这段时间画面一直在动;
 *   · 而 `stable` 计数恒为 0 —— 这套力场本来就不收敛到静止, 只是在小幅震荡。
 *     所以"等它自己停"这条路从一开始就不存在。
 *
 * 现在:内容变了 → 同步算完 → 画一帧 → 停。用户看到的是一张静止的图,
 * 而不是一段永远播不完的动画。
 */
const LAYOUT_ITERS = 400;

/* Stable hash: same id always produces same seed values — no more jumping nodes */
function hashId(id: string): { angle: number; radius: number; z: number } {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) - h) + id.charCodeAt(i);
    h |= 0;
  }
  const angle = ((h & 0xffff) / 0xffff) * Math.PI * 2;
  const radius = 80 + (((h >>> 16) & 0xffff) / 0xffff) * 140;
  const z = ((h & 0x3ff) / 0x3ff - 0.5) * 120;
  return { angle, radius, z };
}

function buildGraphData(
  objects: Props['objects'],
  people: NonNullable<Props['people']>,
  terminals: Props['terminals'],
  facts: Props['facts'],
  tasks: NonNullable<Props['tasks']>,
  places: Props['places'],
  colorMode: Props['colorMode'],
  deviceColors: Props['deviceColors']
): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const seen = new Set<string>();
  const terminalNameSet = new Set(terminals.map((t) => t.name));

  const deviceColor = (deviceId?: string) =>
    colorMode === 'device' && deviceId ? (deviceColors?.[deviceId] ?? NODE_COLORS.object) : undefined;

  const addNode = (id: string, label: string, type: GraphNode['type'], size: number, color?: string) => {
    if (seen.has(id) || !label) return;
    seen.add(id);
    const { angle, radius, z } = hashId(id);
    nodes.push({
      id, label, type,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius * 0.6,
      z,
      vx: 0, vy: 0, vz: 0,
      size,
      color: color ?? NODE_COLORS[type],
    });
  };

  places.forEach((p) => { addNode('place-' + p.id, p.name, 'place', 11, deviceColor(p.deviceId)); });
  objects.forEach((o) => {
    // 大小用真实观测次数(nObs);没有 nObs 才退回 confidence(旧数据形状)。
    const size = o.nObs != null ? 8 + Math.min(6, Math.sqrt(o.nObs)) : 8 + o.confidence * 6;
    addNode(o.id, o.name, 'object', size, deviceColor(o.deviceId));
    const termId = 'term-' + o.detectedBy;
    addNode(termId, o.detectedBy, 'terminal', 10);
    // 这条边唯一能说的真话是"看过几次"。看过 1 次和看过 40 次在台面上必须不一样 ——
    // 那正是判断一条记录可不可信的东西。缺 nObs(旧数据)就不标, 不猜。
    edges.push({
      source: o.id, target: termId, type: 'detected',
      metric: o.nObs != null ? `观测 ${o.nObs} 次` : undefined,
    });
    // located 边只认精确的 nearestPlace 同名匹配 —— 子串猜测/includes 会画出假边。
    places.forEach((p) => {
      if (o.location && o.location === p.name) {
        // 距离是【算出来的】: 两端都在同一张图的 map 帧里, 各自带着真坐标。
        // 缺坐标就不标 —— 以前那个 "距离: 37m" 是哈希, 而这套图上的物体大多在几米内,
        // 那个数字连量级都不对, 却看起来最像读数。
        const d = (o.x != null && o.y != null && p.x != null && p.y != null)
          ? Math.hypot(o.x - p.x, o.y - p.y) : null;
        edges.push({
          source: o.id, target: 'place-' + p.id, type: 'located',
          metric: d != null ? `${d.toFixed(1)} m` : undefined,
        });
      }
    });
  });
  people.forEach((p) => {
    addNode(p.id, p.name, 'person', 12);
    p.syncedTerminals.forEach((t) => {
      const termId = 'term-' + t;
      addNode(termId, t, 'terminal', 10);
      edges.push({ source: p.id, target: termId, type: 'synced' });
    });
  });
  terminals.forEach((t) => { addNode('term-' + t.name, t.name, 'terminal', 10); });
  tasks.forEach((task) => {
    addNode('task-' + task.id, task.name, 'task', 10);
    task.assignees.forEach((a) => {
      const termId = 'term-' + a;
      if (seen.has(termId)) edges.push({ source: 'task-' + task.id, target: termId, type: 'assigned' });
      else if (terminalNameSet.has(a)) {
        addNode(termId, a, 'terminal', 10);
        edges.push({ source: 'task-' + task.id, target: termId, type: 'assigned' });
      }
    });
    places.forEach((p) => {
      if (task.name.includes(p.name) || p.name.includes(task.name.slice(0, 2))) {
        edges.push({ source: 'task-' + task.id, target: 'place-' + p.id, type: 'located' });
      }
    });
  });
  facts.forEach((f) => {
    addNode(f.id, f.content.length > 12 ? f.content.slice(0, 12) + '...' : f.content, 'fact', 7, deviceColor(f.deviceId));
    const srcId = 'term-' + f.source;
    if (seen.has(srcId)) {
      edges.push({
        source: f.id, target: srcId, type: 'derived',
        // 事实自带置信度(设备侧记的)。这是本图上唯一一个真叫"置信度"的量,
        // 而旧版把这三个字贴在了每一条边上。
        metric: f.confidence != null ? `置信度 ${Math.round(f.confidence * 100)}%` : undefined,
      });
    }
  });

  return { nodes, edges };
}

/* --- Main Component --- */
export default function KnowledgeGraph({ objects, people, terminals, facts, tasks, places, colorMode, deviceColors }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMatches, setSearchMatches] = useState<string[]>([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const animRef = useRef<number>(0);
  const nodesRef = useRef<GraphNode[]>([]);
  const edgesRef = useRef<GraphEdge[]>([]);
  const mouseRef = useRef({ x: 0, y: 0, down: false, px: 0, py: 0, button: 0 });
  const dragRef = useRef<GraphNode | null>(null);
  const hoverRef = useRef<GraphNode | null>(null);
  const timeRef = useRef(0);
  const cameraAngleRef = useRef(0);
  const angularVelocityRef = useRef(0);
  const zoomRef = useRef(1);
  const selectedNodeRef = useRef<GraphNode | null>(null);
  const searchMatchesRef = useRef<string[]>([]);
  const searchFlashRef = useRef(0);
  const isDraggingBackgroundRef = useRef(false);
  const lastDragXRef = useRef(0);
  const targetAngleRef = useRef<number | null>(null);
  const targetZoomRef = useRef<number | null>(null);
  const autoCenterActiveRef = useRef(false);
  const scanLineRef = useRef(0); /* 0..1 progress of HUD scan line */
  /* Selection ripple effect */
  const selectionRippleRef = useRef<{ startTime: number } | null>(null);
  /**
   * ★ 重绘由【有没有东西真的变了】驱动, 不再每帧无条件画。
   *
   * 旧判据里有一项 `haveFlowParticles`, 而粒子在有边时恒存在 ⇒ 那个"跳过重绘"的
   * 分支从挂载起一次都没走到过。判据写了却永远为真, 等于没有判据 —— 这和仓库里
   * 「守卫从没跑过」是同一个形状, 只不过这次的代价是用户眼前一直在闪。
   */
  const needsRenderRef = useRef(true);
  const markDirty = useCallback(() => { needsRenderRef.current = true; }, []);
  /** relayout 定义在下面(它要用 layoutStep), 这里先留个转接口。
   *
   *  ★★ 赋值必须在 **render 期间**做, 不能放进 useEffect:
   *  effect 按定义顺序执行, 而【合并数据那个 effect 定义在更前面】—— 它首次挂载时
   *  读到的会是这里的初始值。上一版就是这么翻的车:初始值是个空函数, 于是
   *  **首屏那次布局根本没发生**, 画面停在种子位置(几团重叠的光斑), 缩放也还钉在 100%,
   *  而我盯着"renders=2、画面不再变"以为修好了 —— 静止的错图和静止的对图长得一样。 */
  const relayoutRef = useRef<(iters?: number) => void>(() => {});

  const graphData = useMemo(
    () => buildGraphData(objects, people ?? [], terminals, facts, tasks ?? [], places, colorMode, deviceColors),
    [objects, people, terminals, facts, tasks, places, colorMode, deviceColors]
  );

  /**
   * 图【内容】的指纹。重绘与重新收敛都必须由它驱动, 不能由对象身份驱动。
   *
   * ★★ 这是"一直闪动"的第二个根因, 而且比第一个更隐蔽。父组件每次 re-render 都会
   *    用 toGraphProps() **新建一遍数组**, 于是 useMemo 的依赖(数组身份)每次都变,
   *    graphData 变成新对象, 下面这个 effect 就跑一遍并标脏 —— 哪怕数据一个字没改。
   *    页面上任何一个每秒跳一下的东西(顶栏的钟就是)都会把整张画布重画。
   *    实测:静置时 12 秒里重绘 19 次, 间隔 93~555ms 不规则 —— 正是 re-render 的节奏。
   *
   *    所以判据换成【内容】: 节点 id + 大小 + 边 + 边上的真值。内容没变就什么都不做,
   *    连脏标记都不设。
   *
   * ★ 我第一次"修完再测"报的是 0 次/3 秒, 那是采样窗口太短撞上了空档 ——
   *   一个"我没测到"被当成了"它不存在"(§9b)。这次的窗口是 60 秒。
   */
  const contentKey = useMemo(
    () => graphData.nodes.map((n) => `${n.id}:${n.size.toFixed(1)}`).join('|') + '#'
      + graphData.edges.map((e) => `${e.source}>${e.target}:${e.metric ?? ''}`).join('|'),
    [graphData]
  );

  /* Merge new graph data into refs, preserving positions for existing nodes */
  const lastKeyRef = useRef<string>('');
  useEffect(() => {
    if (lastKeyRef.current === contentKey) return;   // 内容没变 —— 不重绘, 不重新收敛
    lastKeyRef.current = contentKey;
    const prevMap = new Map(nodesRef.current.map((n) => [n.id, n]));
    const merged = graphData.nodes.map((n) => {
      const prev = prevMap.get(n.id);
      if (prev) {
        return { ...n, x: prev.x, y: prev.y, z: prev.z, vx: 0, vy: 0, vz: 0 };
      }
      return n;
    });
    // ★ 先把【旧】边集记下来再替换。旧代码在替换之后才取 prevEdgeKeys,
    //   于是"新边"永远算出 0 条 —— 拓扑变了却不重新收敛, 新边就那么斜挂着。
    const prevEdgeKeys = new Set(edgesRef.current.map((e) => `${e.source}::${e.target}`));
    const newEdgeCount = graphData.edges.filter((e) => !prevEdgeKeys.has(`${e.source}::${e.target}`)).length;
    nodesRef.current = merged;
    edgesRef.current = graphData.edges;

    // 拓扑变了才重排:只是某个物体的观测次数 +1 时, 让整张图重新洗牌毫无意义,
    // 还会把用户刚看清的布局打散。
    if (newEdgeCount > 0 || graphData.nodes.length !== prevMap.size) {
      relayoutRef.current();
    } else {
      markDirty();
    }
  }, [contentKey, graphData, markDirty]);

  useEffect(() => {
    selectedNodeRef.current = selectedNode;
    markDirty();
  }, [selectedNode, markDirty]);
  useEffect(() => {
    searchMatchesRef.current = searchMatches;
    if (searchMatches.length > 0) searchFlashRef.current = performance.now();
    markDirty();
  }, [searchMatches, markDirty]);

  /* ★ 这里原来有一个 effect, 按边名哈希出 latency/带宽/置信度/距离 —— 全是编的,
     见 GraphEdge.metric 的注释。真值现在在 buildGraphData 里【算】出来, 没有就没有。 */

  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  const project = useCallback(
    (node: GraphNode, width: number, height: number, angle: number, zoom: number) => {
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const rx = node.x * cosA - node.z * sinA;
      const rz = node.x * sinA + node.z * cosA;
      const scale = 450 / (450 + rz) * zoom;
      return { sx: width / 2 + rx * scale, sy: height / 2 + node.y * scale, scale, rz };
    },
    []
  );

  const render = useCallback(
    (angle: number, time: number, zoom: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      /* ===== BACKGROUND ===== */
      ctx.fillStyle = '#080d1a';
      ctx.fillRect(0, 0, w, h);

      const vignette = ctx.createRadialGradient(w / 2, h / 2, w * 0.25, w / 2, h / 2, w * 0.9);
      vignette.addColorStop(0, 'rgba(12,20,40,0)');
      vignette.addColorStop(1, 'rgba(3,7,18,0.7)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, w, h);

      /* Dot grid */
      const gs = 36;
      ctx.fillStyle = 'rgba(56,72,100,0.06)';
      for (let gx = gs; gx < w; gx += gs) {
        for (let gy = gs; gy < h; gy += gs) {
          ctx.beginPath();
          ctx.arc(gx, gy, 0.7, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      /* ★ 这里原来有 55 个"环境闪烁点"(alpha = sin(time)), 已删。
         它们和下面删掉的扫描线/呼吸环/流动粒子一起, 让这块画布【每一帧都在变】——
         用户看到的"一直闪动"就是它们四个叠加的结果。而这一页是给人【读数据】的:
         一张永不静止的图, 眼睛没有一处可以停。装饰性动画在这里是负价值。 */

      /* ===== PROJECTION ===== */
      const projected = nodesRef.current.map((n) => ({
        node: n,
        ...project(n, w, h, angle, zoom),
      }));
      const projectionMap = new Map(projected.map((p) => [p.node.id, p]));
      /* ===== HEATMAP: node cluster density ===== */
      const heatColors = [
        'rgba(6,182,212,',   /* cyan */
        'rgba(99,102,241,',  /* indigo */
        'rgba(236,72,153,',  /* pink */
        'rgba(245,158,11,',  /* amber */
      ];
      projected.forEach(({ sx, sy, scale, node }) => {
        if (scale < 0.15) return;
        const hr = 90 * scale;
        const hGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, hr);
        const base = node.color;
        hGrad.addColorStop(0, hexToRgba(base, 0.035));
        hGrad.addColorStop(0.5, hexToRgba(base, 0.012));
        hGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.beginPath();
        ctx.arc(sx, sy, hr, 0, Math.PI * 2);
        ctx.fillStyle = hGrad;
        ctx.fill();
      });

      const hoveredId = hoverRef.current?.id;
      const selectedId = selectedNodeRef.current?.id;
      const connectedEdgeIndices: number[] = [];
      const connectedNodeIds = new Set<string>();
      const matchedIds = new Set(searchMatchesRef.current);
      const isSearchMode = matchedIds.size > 0;
      const flashAge = time - searchFlashRef.current;
      const flashIntensity = isSearchMode
        ? Math.max(0, Math.min(1, 2 - flashAge / 800)) * (0.5 + 0.5 * Math.sin(flashAge * 0.012))
        : 0;

      if (hoveredId) {
        edgesRef.current.forEach((edge, i) => {
          if (edge.source === hoveredId || edge.target === hoveredId) {
            connectedEdgeIndices.push(i);
            connectedNodeIds.add(edge.source === hoveredId ? edge.target : edge.source);
          }
        });
        connectedNodeIds.add(hoveredId);
      } else if (selectedId) {
        edgesRef.current.forEach((edge, i) => {
          if (edge.source === selectedId || edge.target === selectedId) {
            connectedEdgeIndices.push(i);
            connectedNodeIds.add(edge.source === selectedId ? edge.target : edge.source);
          }
        });
        connectedNodeIds.add(selectedId);
      }

      /* ===== EDGES ===== */
      edgesRef.current.forEach((edge, idx) => {
        const from = projectionMap.get(edge.source);
        const to = projectionMap.get(edge.target);
        if (!from || !to) return;
        const highlighted = connectedEdgeIndices.includes(idx);
        const isMatchEdge = matchedIds.has(edge.source) && matchedIds.has(edge.target);
        const isDimmed = (hoveredId || selectedId) && !highlighted;

        if (!isDimmed) {
          /* Glow pass */
          const glowAlpha = highlighted ? 0.22 : isMatchEdge ? 0.16 : 0.05;
          const glowColor = highlighted || isMatchEdge
            ? EDGE_FLOW_COLORS[edge.type]
            : 'rgba(100,116,139,1)';
          ctx.beginPath();
          ctx.moveTo(from.sx, from.sy);
          ctx.lineTo(to.sx, to.sy);
          ctx.strokeStyle = glowColor.replace(/[\d.]+\)$/, `${glowAlpha})`);
          ctx.lineWidth = highlighted ? 6 : isMatchEdge ? 4 : 2.5;
          ctx.stroke();

          /* Core line */
          const coreAlpha = highlighted ? 0.45 : isMatchEdge ? 0.38 : 0.1;
          ctx.beginPath();
          ctx.moveTo(from.sx, from.sy);
          ctx.lineTo(to.sx, to.sy);
          ctx.strokeStyle = glowColor.replace(/[\d.]+\)$/, `${coreAlpha})`);
          ctx.lineWidth = highlighted ? 1.4 : isMatchEdge ? 1 : 0.5;
          ctx.stroke();
        }

        /* ★ 这里原来给几乎每条边画一串带彗尾的流动粒子。删掉的理由有两条:
             ① 它让下面那个"没变化就跳过重绘"的判据【永远为真】—— 只要有边就有粒子,
                于是这块画布从挂载起就没有停过;
             ② 它画的是"数据在流动", 而这张图上的边是【静态的记忆关系】,
                没有任何东西在沿着它们流。动效在这里同时是耗电和谎话。 */
        /* ===== 边上的真值标签 =====
           只在【这条边被 hover 或选中牵连】时出现, 而不是像以前那样默认给 81 条边
           全都贴上 —— 那既是满屏标签雨(截图里那片"置信度: xx%"), 也让人以为
           每条边都有读数。没有真值的边此刻什么都不画。 */
        if (edge.metric && (highlighted || isMatchEdge)) {
          const mx = (from.sx + to.sx) / 2;
          const my = (from.sy + to.sy) / 2;
          const txt = edge.metric;
          ctx.font = '9px "Inter", monospace';
          const tw = ctx.measureText(txt).width + 8;
          const th = 14;
          ctx.fillStyle = 'rgba(8,13,26,0.82)';
          ctx.strokeStyle = EDGE_FLOW_COLORS[edge.type];
          ctx.lineWidth = 1;
          const rx = 4;
          const px1 = mx - tw / 2;
          const py1 = my - th - 4;
          ctx.beginPath();
          ctx.moveTo(px1 + rx, py1);
          ctx.lineTo(px1 + tw - rx, py1);
          ctx.quadraticCurveTo(px1 + tw, py1, px1 + tw, py1 + rx);
          ctx.lineTo(px1 + tw, py1 + th - rx);
          ctx.quadraticCurveTo(px1 + tw, py1 + th, px1 + tw - rx, py1 + th);
          ctx.lineTo(px1 + rx, py1 + th);
          ctx.quadraticCurveTo(px1, py1 + th, px1, py1 + th - rx);
          ctx.lineTo(px1, py1 + rx);
          ctx.quadraticCurveTo(px1, py1, px1 + rx, py1);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = '#e2e8f0';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(txt, px1 + 5, py1 + th / 2 + 0.5);
        }
      });

      /* ===== NODES ===== */
      const labelQueue: { text: string; sx: number; sy: number; fontSize: number; color: string; priority: number; always: boolean }[] = [];
      projected.sort((a, b) => a.rz - b.rz);
      projected.forEach(({ node, sx, sy, scale }) => {
        if (scale <= 0.05) return;
        const isHovered = hoveredId === node.id;
        const isSelected = selectedId === node.id;
        const isConnected = connectedNodeIds.has(node.id);
        const isMatch = matchedIds.has(node.id);
        const nodeAlpha = (hoveredId || selectedId) && !isConnected ? 0.22 : 1;
        const r = Math.max(1, node.size * scale);
        /* 静态相位:环的半径由【节点身份】决定, 不由时间决定 —— 于是它们不再呼吸。
           保留两圈是因为它们承担着"这是个节点"的视觉重量, 会动的只是装饰。 */
        const pulsePhase = node.id.charCodeAt(0) * 0.13;

        /* Search match flash ring */
        if (isMatch && flashIntensity > 0.01) {
          const fr = r * (2 + flashIntensity * 2.8);
          ctx.beginPath();
          ctx.arc(sx, sy, fr, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,255,160,${flashIntensity * 0.65})`;
          ctx.lineWidth = 2.5 * (1 + flashIntensity);
          ctx.stroke();
        }

        /* Outer pulse ring 1 */
        const pr1 = r * (1.55 + 0.18 * Math.sin(pulsePhase));
        ctx.beginPath();
        ctx.arc(sx, sy, pr1, 0, Math.PI * 2);
        ctx.strokeStyle = hexToRgba(node.color, (isHovered ? 0.55 : 0.28) * nodeAlpha);
        ctx.lineWidth = isHovered ? 2.2 : 1.3;
        ctx.stroke();

        /* Outer pulse ring 2 (larger, fainter) */
        const pr2 = r * (1.95 + 0.18 * Math.sin(pulsePhase + 1.7));
        ctx.beginPath();
        ctx.arc(sx, sy, pr2, 0, Math.PI * 2);
        ctx.strokeStyle = hexToRgba(node.color, (isHovered ? 0.3 : 0.12) * nodeAlpha);
        ctx.lineWidth = 0.8;
        ctx.stroke();

        /* Glow aura */
        const glowSize = isHovered ? 4.8 : isMatch ? 4 : 3;
        const glowGrad = ctx.createRadialGradient(sx, sy, r * 0.15, sx, sy, r * glowSize);
        glowGrad.addColorStop(0, hexToRgba(node.color, 0.65 * nodeAlpha));
        glowGrad.addColorStop(0.4, hexToRgba(node.color, 0.22 * nodeAlpha));
        glowGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.beginPath();
        ctx.arc(sx, sy, r * glowSize, 0, Math.PI * 2);
        ctx.fillStyle = glowGrad;
        ctx.fill();

        /* Shell */
        ctx.beginPath();
        ctx.arc(sx, sy, r + 2.5, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(node.color, 0.25 * nodeAlpha);
        ctx.fill();

        /* Core gradient */
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        const coreGrad = ctx.createRadialGradient(sx - r * 0.25, sy - r * 0.25, 0, sx, sy, r);
        coreGrad.addColorStop(0, hexToRgba(node.color, 1));
        coreGrad.addColorStop(0.55, hexToRgba(node.color, 0.8));
        coreGrad.addColorStop(1, hexToRgba(node.color, 0.45));
        ctx.fillStyle = coreGrad;
        ctx.fill();

        /* Bright center */
        ctx.beginPath();
        ctx.arc(sx, sy, r * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.9 * nodeAlpha})`;
        ctx.fill();

        /* Hover highlight ring */
        if (isHovered) {
          ctx.beginPath();
          ctx.arc(sx, sy, r + 7, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.55)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        /* 标签不在这里画 —— 收集起来, 等所有节点画完再统一避让(见下)。 */
        if (scale > 0.55 || isHovered || isSelected || isMatch) {
          labelQueue.push({
            text: node.label.length > 8 ? node.label.slice(0, 7) + '\u2026' : node.label,
            sx, sy: sy + r + Math.max(9, 11 * scale) + 6,
            fontSize: Math.max(9, 11 * scale),
            color: isMatch ? '#fef08a' : isHovered ? '#ffffff' : 'rgba(203,213,225,0.85)',
            /* 重要度决定谁有优先权占位: 被 hover/选中/命中的永远画,
               其余按节点大小(= 真实观测次数)排 —— 看得多的那个名字更该留在台面上。 */
            priority: (isHovered || isSelected || isMatch) ? Infinity : node.size * scale,
            always: isHovered || isSelected || isMatch,
          });
        }
      });

      /* ===== 标签避让 =====
         62 个节点里有一半挤在中央, 名字曾经互相压成一团("笔记本电脑"叠着"背包"叠着
         "台灯"), 每一个都看不清 —— 那等于一个都没标。这里按重要度排序, 后来的标签
         如果和已放下的框相交就【不画】。少标几个但每个都读得出来, 强过全标而全糊。
         hover/选中/搜索命中的那几个无条件画:那正是用户此刻在问的东西。 */
      const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];
      labelQueue.sort((a, b) => b.priority - a.priority);
      labelQueue.forEach((L) => {
        ctx.font = `600 ${L.fontSize}px "Inter", system-ui, sans-serif`;
        const tw = ctx.measureText(L.text).width;
        const box = { x1: L.sx - tw / 2 - 2, y1: L.sy - L.fontSize, x2: L.sx + tw / 2 + 2, y2: L.sy + 3 };
        const hit = placed.some((q) => !(box.x2 < q.x1 || box.x1 > q.x2 || box.y2 < q.y1 || box.y1 > q.y2));
        if (hit && !L.always) return;
        placed.push(box);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        /* 深色描边:标签常常落在发光的节点上, 没有描边时浅灰字会糊进光晕里。 */
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(8,13,26,0.85)';
        ctx.strokeText(L.text, L.sx, L.sy);
        ctx.fillStyle = L.color;
        ctx.fillText(L.text, L.sx, L.sy);
      });

      /* ===== SELECTION RIPPLE ===== */
      const ripple = selectionRippleRef.current;
      if (ripple && selectedId) {
        const rippleNode = nodesRef.current.find((n) => n.id === selectedId);
        if (rippleNode) {
          const rp = project(rippleNode, w, h, angle, zoom);
          const elapsed = time - ripple.startTime;
          if (elapsed < 1400) {
            const progress = elapsed / 1400;
            const rr = progress * 110 * zoom;
            const ra = (1 - progress) * 0.6;
            ctx.beginPath();
            ctx.arc(rp.sx, rp.sy, rr, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(255,255,255,${ra})`;
            ctx.lineWidth = 3 * (1 - progress);
            ctx.stroke();

            const progress2 = Math.max(0, (elapsed - 180) / 1220);
            if (progress2 > 0 && progress2 < 1) {
              const rr2 = progress2 * 85 * zoom;
              const ra2 = (1 - progress2) * 0.4;
              ctx.beginPath();
              ctx.arc(rp.sx, rp.sy, rr2, 0, Math.PI * 2);
              ctx.strokeStyle = `rgba(255,255,255,${ra2})`;
              ctx.lineWidth = 1.8 * (1 - progress2);
              ctx.stroke();
            }
          } else {
            selectionRippleRef.current = null;
          }
        }
      }

      /* ★ 这里原来有一条每 6.6 秒扫过全屏的 HUD 扫描线(含刻度与拖尾), 已删。
         它是全画布重绘的最后一个理由:只要它在, 这块画布就永远静不下来。 */
    },
    [project]
  );

  /**
   * 一步 Fruchterman–Reingold。★★ 为什么把原来那套自制力场整个换掉:
   *
   * 原来的斥力只在 `dist < (a.size+b.size)*9` 以内起作用, 超出这个半径就【只剩】
   * 弹簧和向心力。这在动画里看不出来 —— 那套力场从不收敛, 画面永远在 churn。
   * 而一旦真的让它算到收敛(本次改动), 结果是**退化的**: 各连通分量互相塌在一起,
   * 102 个节点叠成 5 坨光斑。换句话说, 旧实现之所以"看着还行", 全靠它永远算不完。
   *
   * FR 的斥力是全局的 k²/d、引力是沿边的 d²/k, 配一条降温曲线 ⇒ 有限步收敛,
   * 且节点间保持 ~k 的间距(k 由布局空间面积与节点数定)。这是教科书解法, 不是调参。
   */
  const layoutStep = useCallback((k: number, temp: number) => {
    const nodes = nodesRef.current;
    const n = nodes.length;
    if (!n) return;
    const dispX = new Float64Array(n), dispY = new Float64Array(n), dispZ = new Float64Array(n);
    const idx = new Map(nodes.map((nd, i) => [nd.id, i]));

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = nodes[i], b = nodes[j];
        let dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
        let d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (d < 0.01) {   // 完全重合:给一个【确定性】的小偏移, 不用随机数(否则每次布局都不一样)
          dx = ((i % 7) - 3) * 0.1; dy = ((j % 5) - 2) * 0.1; dz = 0.05; d = 0.2;
        }
        const f = (k * k) / d;
        const ux = dx / d, uy = dy / d, uz = dz / d;
        dispX[i] += ux * f; dispY[i] += uy * f; dispZ[i] += uz * f;
        dispX[j] -= ux * f; dispY[j] -= uy * f; dispZ[j] -= uz * f;
      }
    }
    for (const e of edgesRef.current) {
      const i = idx.get(e.source), j = idx.get(e.target);
      if (i === undefined || j === undefined) continue;
      const a = nodes[i], b = nodes[j];
      const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.01;
      const f = (d * d) / k;
      const ux = dx / d, uy = dy / d, uz = dz / d;
      dispX[i] -= ux * f; dispY[i] -= uy * f; dispZ[i] -= uz * f;
      dispX[j] += ux * f; dispY[j] += uy * f; dispZ[j] += uz * f;
    }
    for (let i = 0; i < n; i++) {
      const nd = nodes[i];
      // 很弱的向心力:只防孤立节点飘到天边, 强到能压过斥力就又会塌回一坨。
      const dx = dispX[i] - nd.x * 0.012;
      const dy = dispY[i] - nd.y * 0.012;
      const dz = dispZ[i] - nd.z * 0.012;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
      const lim = Math.min(d, temp) / d;   // 单步位移不超过当前温度 ⇒ 降温即收敛
      nd.x += dx * lim;
      nd.y += dy * lim * 0.62;             // 画布是横的, 竖直方向压扁一点更好读
      nd.z += dz * lim * 0.5;
      nd.vx = 0; nd.vy = 0; nd.vz = 0;
    }
  }, []);

  const layoutStepRef = useRef(layoutStep);
  useEffect(() => { layoutStepRef.current = layoutStep; }, [layoutStep]);

  /**
   * 算完之后【重新取景】, 让整张图落在画布里。
   *
   * ★ 少了这一步就是上一版的样子:布局一次算完之后节点被斥力推得很开,
   *   而缩放还钉在 100%, 于是画面里只剩中间那几个点 —— 图是静止了, 也看不见了。
   *   以前每帧都在 churn, 从没真正收敛到那个尺度, 所以这个缺陷被掩盖着。
   *   "算出布局"和"把布局放进取景框"是两件事, 缺一件都不成立。
   */
  const fitView = useCallback(() => {
    const nodes = nodesRef.current;
    const canvas = canvasRef.current;
    if (!nodes.length || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || 800, h = rect.height || 500;
    let maxX = 1, maxY = 1;
    for (const n of nodes) {
      // 用最坏情况的透视缩放(最近的那一层)来估, 免得近处的节点仍被切掉。
      const persp = 450 / (450 + Math.min(0, n.z));
      maxX = Math.max(maxX, Math.abs(n.x) * persp);
      maxY = Math.max(maxY, Math.abs(n.y) * persp);
    }
    // 留 18% 余量给节点标签(它们画在点的下方)。
    const zoom = Math.min(w / 2 / (maxX * 1.18), h / 2 / (maxY * 1.30));
    zoomRef.current = Math.max(0.15, Math.min(2.2, zoom));
    setZoomLevel(zoomRef.current);
  }, []);

  /**
   * 把布局【一次算完】。内容变化或拖拽时调用, 不挂在每帧上。
   * 400 步在 62 个节点上是几十毫秒的事(一帧的量级), 而它换来的是之后的完全静止。
   */
  const relayout = useCallback((iters = LAYOUT_ITERS) => {
    const n = nodesRef.current.length;
    if (!n) return;
    // k = 理想间距, 由"给这些节点多大地方"定。1100x700 是**虚拟布局空间**, 与画布像素无关
    // —— 画布尺寸由 fitView 的缩放吸收, 所以换个窗口大小不会让图重排。
    const k = Math.sqrt((1100 * 700) / n);
    let temp = 1100 / 8;
    const cool = Math.pow(0.02, 1 / Math.max(1, iters));   // 末温 ≈ 初温的 2%
    for (let i = 0; i < iters; i++) {
      layoutStepRef.current(k, temp);
      temp *= cool;
    }
    fitView();
    needsRenderRef.current = true;
  }, [fitView]);
  relayoutRef.current = relayout;   // 见上:render 期间赋值, 不能推迟到 effect

  const renderRef = useRef(render);
  useEffect(() => { renderRef.current = render; }, [render]);

  useEffect(() => {
    let lastTime = 0;
    let running = true;
    /**
     * ★ 诊断出口。这块画布"为什么在画"从外面量不出来 —— 两轮都只能猜:
     *   第一次靠删装饰(错的), 第二次靠猜父组件 re-render(也不是全部)。
     *   现在把【每一帧的判据结果】计数, 浏览器里读 window.__oxmanGraphDebug 就知道
     *   是 sim 没停、还是有人在标脏、还是相机在动。开销是几个整数自增。
     */
    const dbg = { frames: 0, renders: 0, why: {} as Record<string, number> };
    (window as unknown as { __oxmanGraphDebug?: unknown }).__oxmanGraphDebug = dbg;
    let lastAngle = cameraAngleRef.current;
    let lastZoom = zoomRef.current;

    const animate = (timestamp: number) => {
      if (!running) return;
      const dt = Math.min(timestamp - lastTime, 32);
      lastTime = timestamp;
      timeRef.current = timestamp;
      if (dt > 0) {
        if (targetAngleRef.current !== null && autoCenterActiveRef.current) {
          const lerpFactor = 0.04 * dt;
          cameraAngleRef.current +=
            (targetAngleRef.current - cameraAngleRef.current) * lerpFactor;
          if (targetZoomRef.current !== null) {
            zoomRef.current += (targetZoomRef.current - zoomRef.current) * lerpFactor;
            setZoomLevel(zoomRef.current);
          }
          if (
            Math.abs(targetAngleRef.current - cameraAngleRef.current) < 0.002 &&
            (targetZoomRef.current === null ||
              Math.abs(targetZoomRef.current - zoomRef.current) < 0.004)
          ) {
            autoCenterActiveRef.current = false;
            targetAngleRef.current = null;
            targetZoomRef.current = null;
          }
        } else if (!mouseRef.current.down) {
          if (Math.abs(angularVelocityRef.current) > 0.00002) {
            cameraAngleRef.current += angularVelocityRef.current * dt;
            angularVelocityRef.current *= 0.96;
          } else if (angularVelocityRef.current !== 0) {
            angularVelocityRef.current = 0;
          }
          /* NO MORE auto-rotation — this was the source of constant jitter */
        }
        /* ★★ 这里【不再跑力导】。布局由 relayout() 在内容变化/拖拽时一次算完。
           旧写法是每帧推 2 步、并把"没稳定"直接写进重绘判据 —— 而这套力场不收敛,
           实测 `stable` 计数恒为 0, 于是"每帧都画"成了常态(实测 5.8 帧/秒, 每帧
           62 个径向渐变)。用户看到的持续闪动就是这一行。
           布局是一次计算, 不是一段动画。 */
      }

      /* 还在动的东西:选中涟漪与搜索闪烁都是【有始有终】的短动画,
         它们在播的时候要继续画, 播完就该停 —— 这和常驻装饰动画的区别就在这。 */
      const ripple = selectionRippleRef.current;
      const rippleActive = ripple !== null && timestamp - ripple.startTime < 1400;
      const flashActive = searchMatchesRef.current.length > 0 && timestamp - searchFlashRef.current < 1600;
      const angleChanged = Math.abs(cameraAngleRef.current - lastAngle) > 0.00001;
      const zoomChanged = Math.abs(zoomRef.current - lastZoom) > 0.0001;
      const hasInertia = Math.abs(angularVelocityRef.current) > 0.00002;

      const why = needsRenderRef.current ? 'dirty'
        : angleChanged ? 'angle' : zoomChanged ? 'zoom'
        : hasInertia ? 'inertia' : autoCenterActiveRef.current ? 'autocenter'
        : rippleActive ? 'ripple' : flashActive ? 'flash' : '';
      if (why) {
        needsRenderRef.current = false;
        dbg.renders++; dbg.why[why] = (dbg.why[why] || 0) + 1;
        renderRef.current(cameraAngleRef.current, timestamp, zoomRef.current);
      }
      dbg.frames++;

      lastAngle = cameraAngleRef.current;
      lastZoom = zoomRef.current;
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      running = false;
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  /* ResizeObserver with setTransform (not scale) to prevent DPR stacking */
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const resize = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // ★★ 写 canvas.width/height 会【清空位图】。以前每帧都在画, 所以看不出来;
      //    现在改成"有变化才画", 不补这一下的话, 挂载/改窗口之后就是一块黑板
      //    —— 我上一版正是这么翻的车。尺寸变了 = 内容必须重画一次。
      markDirty();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => observer.disconnect();
  }, [markDirty]);

  const getNodeAt = (mx: number, my: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width,
      h = rect.height;
    const projected = nodesRef.current.map((n) => ({
      node: n,
      ...project(n, w, h, cameraAngleRef.current, zoomRef.current),
    }));
    for (let i = projected.length - 1; i >= 0; i--) {
      const { node, sx, sy, scale } = projected[i];
      const r = node.size * Math.max(0.5, scale) + 4;
      if ((mx - sx) ** 2 + (my - sy) ** 2 < r ** 2) return node;
    }
    return null;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    if (mouseRef.current.down && dragRef.current) {
      // ★ 「拖拽节点」以前【什么都没做】: 这个分支直接 return, 节点纹丝不动。
      //   底部却一直写着"拖拽节点"。承诺了一个不存在的交互, 比不提供更糟 ——
      //   用户会以为是自己没拖对。现在真的把它拖起来。
      //
      //   做法是把屏幕坐标反投影回世界坐标: 深度(rz)保持不变, 于是
      //     rx = (sx - w/2)/scale,  y = (sy - h/2)/scale
      //   再用同一个角度把 (rx, rz) 转回 (x, z) —— 正好是 project() 的逆。
      const n = dragRef.current;
      const angle = cameraAngleRef.current;
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const rz = n.x * sinA + n.z * cosA;
      const scale = 450 / (450 + rz) * zoomRef.current;
      if (scale > 0.02) {
        const rx = (mx - rect.width / 2) / scale;
        n.x = rx * cosA + rz * sinA;
        n.z = -rx * sinA + rz * cosA;
        n.y = (my - rect.height / 2) / scale;
        n.vx = 0; n.vy = 0; n.vz = 0;
        relayoutRef.current(6);   // 邻居跟着让位:同步推几步就够, 不必把整张图扔进动画
      }
      autoCenterActiveRef.current = false;
      targetAngleRef.current = null;
      targetZoomRef.current = null;
      markDirty();
      return;
    }
    if (mouseRef.current.down && isDraggingBackgroundRef.current) {
      // ★ 「拖拽旋转」同样是空的: 旧代码只记下 x, 角度要【松手之后】靠惯性才动。
      //   拖的时候画面不跟手, 手感上就是"拖不动"。现在按位移实时转。
      const dx = e.clientX - lastDragXRef.current;
      cameraAngleRef.current += dx * 0.005;
      lastDragXRef.current = e.clientX;
      autoCenterActiveRef.current = false;
      targetAngleRef.current = null;
      targetZoomRef.current = null;
      markDirty();
      return;
    }
    const node = getNodeAt(mx, my);
    if ((hoverRef.current?.id ?? null) !== (node?.id ?? null)) markDirty();
    hoverRef.current = node || null;
    setHoveredNode(node || null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = node
        ? 'pointer'
        : mouseRef.current.down
        ? 'grabbing'
        : 'grab';
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setSearchMatches([]);
      autoCenterActiveRef.current = false;
      targetAngleRef.current = null;
      targetZoomRef.current = null;
      return;
    }
    const lower = term.toLowerCase();
    const matched = nodesRef.current
      .filter((n) => n.label.toLowerCase().includes(lower))
      .map((n) => n.id);
    setSearchMatches(matched);
    if (matched.length > 0) {
      const matchedNodes = nodesRef.current.filter((n) => matched.includes(n.id));
      let cx = 0,
        cy = 0,
        cz = 0;
      matchedNodes.forEach((n) => {
        cx += n.x;
        cy += n.y;
        cz += n.z;
      });
      cx /= matchedNodes.length;
      cy /= matchedNodes.length;
      cz /= matchedNodes.length;
      const targetAngle = Math.atan2(cz, cx) - Math.PI / 2;
      let normalizedTarget = targetAngle;
      while (normalizedTarget - cameraAngleRef.current > Math.PI)
        normalizedTarget -= Math.PI * 2;
      while (normalizedTarget - cameraAngleRef.current < -Math.PI)
        normalizedTarget += Math.PI * 2;
      targetAngleRef.current = normalizedTarget;
      targetZoomRef.current = 1.4;
      autoCenterActiveRef.current = true;
    }
  };

  const nodeStats = useMemo(() => {
    const counts: Record<string, number> = {};
    graphData.nodes.forEach((n) => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return counts;
  }, [graphData]);

  const typeEntries = [
    { key: 'object', label: '物体', color: NODE_COLORS.object },
    { key: 'person', label: '人物', color: NODE_COLORS.person },
    { key: 'terminal', label: '终端', color: NODE_COLORS.terminal },
    { key: 'task', label: '任务', color: NODE_COLORS.task },
    { key: 'place', label: '地点', color: NODE_COLORS.place },
    { key: 'fact', label: '事实', color: NODE_COLORS.fact },
  ];

  const canvasHeightClass = isFullscreen ? 'h-screen' : 'h-[560px]';
  const canvasRoundedClass = isFullscreen ? 'rounded-none' : 'rounded-lg';

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {/* 只列图中真实存在的类型 —— 人物/任务永不出现在数据里时,图例也不该露脸 */}
          {typeEntries.filter((entry) => (nodeStats[entry.key] || 0) > 0).map((entry) => (
            <div key={entry.key} className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              ></span>
              <span className="text-[9px] text-foreground-500">
                {entry.label}{' '}
                {(nodeStats[entry.key] || 0) > 0 && (
                  <span className="text-foreground-400">{nodeStats[entry.key] || 0}</span>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-[10px]" />
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="搜索节点..."
              className="w-36 pl-7 pr-2 py-1.5 text-[11px] bg-background-50 border border-background-200/70 rounded-md text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-[10px]" />
              </button>
            )}
          </div>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="w-7 h-7 flex items-center justify-center rounded-md bg-background-50 border border-background-200/70 text-foreground-400 hover:text-foreground-600 hover:border-background-300/80 transition-all cursor-pointer"
            title={isFullscreen ? '退出全屏' : '全屏模式'}
          >
            <i
              className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-xs`}
            />
          </button>
          <span className="text-[9px] text-foreground-400">
            {graphData.nodes.length} 节点 · {graphData.edges.length} 关联
          </span>
        </div>
      </div>
      {searchMatches.length > 0 && (
        <div className="mb-2 text-[10px] text-foreground-500 animate-fade-in">
          找到{' '}
          <strong className="text-foreground-700">{searchMatches.length}</strong> 个匹配节点 ·
          镜头已自动居中
        </div>
      )}
      <div
        ref={containerRef}
        className={`relative w-full ${canvasHeightClass} bg-[#080d1a] ${canvasRoundedClass} border border-slate-700/40 overflow-hidden ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          onMouseMove={handleMouseMove}
          onMouseDown={(e) => {
            mouseRef.current = {
              ...mouseRef.current,
              down: true,
              px: e.clientX,
              py: e.clientY,
              button: e.button,
            };
            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;
            const node = getNodeAt(e.clientX - rect.left, e.clientY - rect.top);
            if (node) {
              dragRef.current = node;
              isDraggingBackgroundRef.current = false;
              angularVelocityRef.current = 0;
              autoCenterActiveRef.current = false;
              targetAngleRef.current = null;
              targetZoomRef.current = null;
            } else {
              dragRef.current = null;
              isDraggingBackgroundRef.current = true;
              lastDragXRef.current = e.clientX;
              angularVelocityRef.current = 0;
              autoCenterActiveRef.current = false;
              targetAngleRef.current = null;
              targetZoomRef.current = null;
            }
          }}
          onMouseUp={(e) => {
            const rect = canvasRef.current?.getBoundingClientRect();
            const mx = rect ? e.clientX - rect.left : 0;
            const my = rect ? e.clientY - rect.top : 0;
            const node = rect ? getNodeAt(mx, my) : null;
            const dx = Math.abs(e.clientX - mouseRef.current.px),
              dy = Math.abs(e.clientY - mouseRef.current.py);
            if (dx < 4 && dy < 4 && node && node === dragRef.current) {
              setSelectedNode((prev) => {
                const newSelected = prev?.id === node.id ? null : node;
                if (newSelected) {
                  selectionRippleRef.current = { startTime: performance.now() };
                }
                return newSelected;
              });
            }
            mouseRef.current.down = false;
            dragRef.current = null;
            isDraggingBackgroundRef.current = false;
            markDirty();
          }}
          onMouseLeave={() => {
            hoverRef.current = null;
            setHoveredNode(null);
            markDirty();
            mouseRef.current.down = false;
            dragRef.current = null;
            isDraggingBackgroundRef.current = false;
          }}
          onWheel={(e) => {
            e.preventDefault();
            autoCenterActiveRef.current = false;
            targetAngleRef.current = null;
            targetZoomRef.current = null;
            zoomRef.current = Math.max(
              0.4,
              Math.min(2.2, zoomRef.current + (e.deltaY > 0 ? -0.06 : 0.06))
            );
            setZoomLevel(zoomRef.current);
            markDirty();
          }}
        />
        {hoveredNode && (
          <div
            className="absolute z-10 pointer-events-none bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-lg px-3 py-2 animate-fade-in"
            style={{ left: '50%', top: 12, transform: 'translateX(-50%)' }}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: hoveredNode.color }}
              ></span>
              <span className="text-[10px] font-semibold text-slate-200">
                {hoveredNode.label}
              </span>
              <span className="text-[9px] text-slate-400 px-1 py-0.5 bg-slate-700/60 rounded capitalize">
                {hoveredNode.type}
              </span>
            </div>
          </div>
        )}
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-slate-800/80 backdrop-blur-sm rounded-full px-2 py-1 border border-slate-600/40 text-[10px] text-slate-400">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-zoom-in-line text-[9px]"></i>
          </span>
          {Math.round(zoomLevel * 100)}%
        </div>
        {isFullscreen && (
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center bg-slate-800/90 backdrop-blur-sm rounded-full border border-slate-600/50 text-slate-300 hover:text-white hover:bg-slate-700 cursor-pointer transition-all"
            title="退出全屏 (Esc)"
          >
            <i className="ri-fullscreen-exit-line text-sm" />
          </button>
        )}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-4 text-[9px] text-slate-400 bg-slate-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-slate-600/40">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-mouse-line text-[9px]"></i>
            </span>
            拖拽节点
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-cursor-line text-[9px]"></i>
            </span>
            点击查看详情
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-drag-move-line text-[9px]"></i>
            </span>
            拖拽旋转
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-zoom-in-line text-[9px]"></i>
            </span>
            滚轮缩放
          </span>
        </div>
      </div>
      {selectedNode && !isFullscreen && (
        <div className="mt-3 p-3 bg-background-50 border border-accent-300/60 rounded-lg animate-fade-in-up">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedNode.color }}
              ></span>
              <span className="text-xs font-bold text-foreground-800">{selectedNode.label}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500 capitalize">
                {selectedNode.type}
              </span>
            </div>
            <button
              onClick={() => setSelectedNode(null)}
              className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            <div>
              <div className="text-[9px] text-foreground-400 mb-0.5">关联关系</div>
              <div className="text-xs text-foreground-700 font-medium">
                {
                  edgesRef.current.filter(
                    (e) => e.source === selectedNode.id || e.target === selectedNode.id
                  ).length
                }{' '}
                条
              </div>
            </div>
            <div>
              <div className="text-[9px] text-foreground-400 mb-0.5">类型</div>
              <div className="text-xs text-foreground-700 capitalize">{selectedNode.type}</div>
            </div>
            <div>
              <div className="text-[9px] text-foreground-400 mb-0.5">节点 ID</div>
              <div className="text-[10px] text-foreground-500 font-mono">
                {selectedNode.id.slice(0, 16)}
              </div>
            </div>
          </div>
          <div className="pt-2 border-t border-background-200/70">
            <div className="text-[9px] text-foreground-400 mb-1.5">直接关联实体</div>
            <div className="flex flex-wrap gap-1.5">
              {edgesRef.current
                .filter(
                  (e) => e.source === selectedNode.id || e.target === selectedNode.id
                )
                .slice(0, 10)
                .map((e) => {
                  const otherId = e.source === selectedNode.id ? e.target : e.source;
                  const otherNode = nodesRef.current.find((n) => n.id === otherId);
                  if (!otherNode) return null;
                  const typeLabel =
                    {
                      detected: '检测',
                      synced: '同步',
                      derived: '推导',
                      assigned: '指派',
                      located: '位于',
                      linked: '关联',
                    }[e.type] || e.type;
                  return (
                    <span
                      key={e.source + e.target}
                      className="text-[9px] px-2 py-0.5 bg-background-100 rounded-full text-foreground-600 flex items-center gap-1"
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: otherNode.color }}
                      ></span>
                      {otherNode.label}
                      <span className="text-foreground-400">· {typeLabel}</span>
                    </span>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}